import { Component, lazy, Suspense, type ComponentType, type ErrorInfo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Switch, Route, Router as WouterRouter, Redirect, useLocation } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import "./i18n";
import { queryClient } from "./lib/queryClient";
import { ThemeProvider } from "./components/theme-provider";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppShell } from "./components/app-shell";
import { AdminShell } from "./components/admin-shell";
import { CookieNotice } from "./components/cookie-notice";
import { AuthProvider, useAuth } from "./lib/auth-context";
import { makeProtectedRoute } from "./lib/protected-route";
import { isStudioPlan } from "./lib/plan";
import { reportReactError } from "./lib/errorReporter";
import { canAccessTeam } from "./lib/team-access";

/**
 * A failed dynamic import almost always means the build was updated and this
 * tab still points at old chunk hashes ("Failed to fetch dynamically imported
 * module"). Reload once to pull the fresh index.html + chunk URLs; guard with
 * sessionStorage so a genuinely broken chunk can't trigger a reload loop.
 */
function lazyWithReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    factory().catch((err: unknown) => {
      const KEY = "chunk-reload-at";
      const now = Date.now();
      const last = Number(sessionStorage.getItem(KEY) ?? "0");
      if (!last || now - last > 10_000) {
        sessionStorage.setItem(KEY, String(now));
        window.location.reload();
        // Never resolve: keep the Suspense fallback up until the reload takes
        // over, so the error screen never flashes before the page reloads.
        return new Promise<{ default: T }>(() => { });
      }
      throw err;
    }),
  );
}

const Login = lazyWithReload(() => import("./pages/landing"));
const Marketing = lazyWithReload(() => import("./pages/marketing"));
const Pricing = lazyWithReload(() => import("./pages/pricing"));
const Contact = lazyWithReload(() => import("./pages/contact"));
const Policy = lazyWithReload(() => import("./pages/policy"));
const Dashboard = lazyWithReload(() => import("./pages/dashboard"));
const Assets = lazyWithReload(() => import("./pages/assets"));
const NewAsset = lazyWithReload(() => import("./pages/new-asset"));
const AssetDetail = lazyWithReload(() => import("./pages/asset-detail"));
const Presets = lazyWithReload(() => import("./pages/presets"));
const Jobs = lazyWithReload(() => import("./pages/jobs"));
const Credits = lazyWithReload(() => import("./pages/credits"));
const Settings = lazyWithReload(() => import("./pages/settings"));
const Notifications = lazyWithReload(() => import("./pages/notifications"));
const CreateUpload = lazyWithReload(() => import("./pages/create-upload"));
const CreateAnalyze = lazyWithReload(() => import("./pages/create-analyze"));
const CreateFormats = lazyWithReload(() => import("./pages/create-formats"));
const CreateProcessing = lazyWithReload(() => import("./pages/create-processing"));
const CreativeSpace = lazyWithReload(() => import("./pages/creative-space"));
const BannerToVideo = lazyWithReload(() => import("./pages/banner-to-video"));
const AdaptiveLocation = lazyWithReload(() => import("./pages/adaptive-location"));
const AdaptiveLocationIngest = lazyWithReload(() => import("./pages/adaptive-location-ingest"));
const AdaptiveLocationCampaigns = lazyWithReload(() => import("./pages/adaptive-location-campaigns"));
const AdaptiveLocationCampaignDetail = lazyWithReload(() => import("./pages/adaptive-location-campaign-detail"));
const AdaptiveLocationTemplates = lazyWithReload(() => import("./pages/adaptive-location-templates"));
const AdaptiveLocationRender = lazyWithReload(() => import("./pages/adaptive-location-render"));
const CreativeSpacePicker = lazyWithReload(() => import("./pages/creative-space-picker"));
const CustomerReviews = lazyWithReload(() => import("./pages/customer-reviews"));
const CustomerReview = lazyWithReload(() => import("./pages/customer-review"));
const Purchase = lazyWithReload(() => import("./pages/customer-purchase"));
const Team = lazyWithReload(() => import("./pages/customer-team"));
const Billing = lazyWithReload(() => import("./pages/customer-billing"));
const ReferralsPage = lazyWithReload(() => import("./pages/referrals"));
const AffiliatePage = lazyWithReload(() => import("./pages/affiliate"));
const Checkout = lazyWithReload(() => import("./pages/customer-checkout"));
const BboxCropTelemetry = lazyWithReload(() => import("./pages/bbox-crop-telemetry"));
const Register = lazyWithReload(() => import("./pages/register"));
const RegisterSingle = lazyWithReload(() => import("./pages/register-single"));
const RegisterBusiness = lazyWithReload(() => import("./pages/register-business"));
const VerifyEmail = lazyWithReload(() => import("./pages/verify-email"));
const ForgotPassword = lazyWithReload(() => import("./pages/forgot-password"));
const ResetPassword = lazyWithReload(() => import("./pages/reset-password"));
const AcceptInvite = lazyWithReload(() => import("./pages/accept-invite"));
const AdminClients = lazyWithReload(() => import("./pages/admin-clients"));
const AdminClientDetail = lazyWithReload(() => import("./pages/admin-client-detail"));
const AdminReconciliation = lazyWithReload(() => import("./pages/admin-reconciliation"));
const AdminReports = lazyWithReload(() => import("./pages/admin-reports"));
const AdminReferrals = lazyWithReload(() => import("./pages/admin-referrals"));
const AdminAffiliates = lazyWithReload(() => import("./pages/admin-affiliates"));
const AdminContact = lazyWithReload(() => import("./pages/admin-contact"));
const AdminAdaptiveLocation = lazyWithReload(() => import("./pages/admin-adaptive-location"));
const AdminSettings = lazyWithReload(() => import("./pages/admin-settings"));
const CreateCanva = lazyWithReload(() => import("./pages/create-canva"));

function RouteFallback() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary" />
    </div>
  );
}

class RouteErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    reportReactError(error, {
      componentStack: info.componentStack ?? undefined,
      boundary: "RouteErrorBoundary",
    });
    // eslint-disable-next-line no-console
    console.error("[RouteErrorBoundary]", error, info);
  }
  render() {
    if (this.state.error) {
      return <RouteErrorFallback />;
    }
    return this.props.children;
  }
}

function RouteErrorFallback() {
  const { t } = useTranslation("common");
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-4 text-center">
      <h2 className="text-lg font-semibold text-foreground">{t("app.loadError")}</h2>
      <p className="text-sm text-muted-foreground max-w-md">{t("app.loadErrorHint")}</p>
      <button
        type="button"
        className="rounded-md border border-border bg-card px-4 py-2 text-sm font-medium hover:bg-muted"
        onClick={() => window.location.reload()}
      >
        {t("app.reload")}
      </button>
    </div>
  );
}

const CANVAS_BASE_RE = /^\/__[a-z0-9-]+-frontend(?=\/|$)/;
const detectedBase =
  typeof window !== "undefined"
    ? CANVAS_BASE_RE.exec(window.location.pathname)?.[0] ?? ""
    : "";
const basePath = detectedBase || import.meta.env.BASE_URL.replace(/\/$/, "");

function FullScreenShell({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

const ProtectedRoute = makeProtectedRoute({ shell: FullScreenShell, basePath });
const ProtectedFullScreen = makeProtectedRoute({ shell: FullScreenShell, basePath });

function HomeRedirect() {
  const { user, isLoading } = useAuth();
  if (isLoading) return <RouteFallback />;
  return <Marketing />;
}

function ProtectedAdminRoute({ component: Component }: { component: React.ComponentType }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return <RouteFallback />;
  if (!user) {
    const current = window.location.pathname + window.location.search;
    const relative =
      basePath && current.startsWith(basePath) ? current.slice(basePath.length) : current;
    if (relative && relative !== "/" && relative !== "") {
      return <Redirect to={`/login?returnTo=${encodeURIComponent(relative)}`} replace />;
    }
    return <Redirect to="/login" replace />;
  }
  if (!user.isSystemAdmin) {
    return <Redirect to="/dashboard" replace />;
  }
  return (
    <AdminShell>
      <Component />
    </AdminShell>
  );
}

function StudioProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return <RouteFallback />;
  if (!user) {
    const current = window.location.pathname + window.location.search;
    const relative =
      basePath && current.startsWith(basePath) ? current.slice(basePath.length) : current;
    if (relative && relative !== "/" && relative !== "") {
      return <Redirect to={`/login?returnTo=${encodeURIComponent(relative)}`} replace />;
    }
    return <Redirect to="/login" replace />;
  }
  // Studio-plan gate mirrors the server `requireStudioPlan`; non-Studio users
  // never see the section, so send them back to the dashboard.
  if (!isStudioPlan(user)) {
    return <Redirect to="/dashboard" replace />;
  }
  return <Component />;
}

function ProtectedTeamRoute() {
  const { user, isLoading } = useAuth();
  if (isLoading) return <RouteFallback />;
  if (!user) {
    const current = window.location.pathname + window.location.search;
    const relative =
      basePath && current.startsWith(basePath) ? current.slice(basePath.length) : current;
    if (relative && relative !== "/" && relative !== "") {
      return <Redirect to={`/login?returnTo=${encodeURIComponent(relative)}`} replace />;
    }
    return <Redirect to="/login" replace />;
  }
  if (!canAccessTeam(user.accountRole)) {
    return <Redirect to="/dashboard" replace />;
  }
  return <Team />;
}

function Routes() {
  const { t } = useTranslation("common");
  return (
    <Suspense fallback={<RouteFallback />}>
      <Switch>
        <Route path="/" component={HomeRedirect} />
        <Route path="/login" component={Login} />
        <Route path="/pricing" component={Pricing} />
        <Route path="/contact" component={Contact} />
        <Route path="/policy" component={Policy} />
        <Route path="/register" component={Register} />
        <Route path="/register/single" component={RegisterSingle} />
        <Route path="/register/business" component={RegisterBusiness} />
        <Route path="/verify-email" component={VerifyEmail} />
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/reset-password" component={ResetPassword} />
        <Route path="/accept-invite" component={AcceptInvite} />
        <Route path="/dashboard" component={() => <ProtectedRoute component={Dashboard} />} />
        <Route path="/assets" component={() => <ProtectedRoute component={Assets} />} />
        <Route path="/assets/new" component={() => <ProtectedRoute component={NewAsset} />} />
        <Route path="/assets/:id" component={() => <ProtectedRoute component={AssetDetail} />} />
        <Route path="/assets/:id/creative-space" component={() => <ProtectedFullScreen component={CreativeSpace} />} />
        <Route path="/creative-space" component={() => <ProtectedRoute component={CreativeSpacePicker} />} />
        <Route path="/banner-to-video" component={() => <ProtectedRoute component={BannerToVideo} />} />
        <Route path="/adaptive-location" component={() => <StudioProtectedRoute component={AdaptiveLocation} />} />
        <Route path="/adaptive-location/templates" component={() => <StudioProtectedRoute component={AdaptiveLocationTemplates} />} />
        <Route path="/adaptive-location/render" component={() => <StudioProtectedRoute component={AdaptiveLocationRender} />} />
        {/* Data processing (split out of Adaptive Location) */}
        <Route path="/data-processing" component={() => <Redirect to="/data-processing/ingest" replace />} />
        <Route path="/data-processing/ingest" component={() => <StudioProtectedRoute component={AdaptiveLocationIngest} />} />
        <Route path="/data-processing/campaigns" component={() => <StudioProtectedRoute component={AdaptiveLocationCampaigns} />} />
        <Route path="/data-processing/campaigns/:id" component={() => <StudioProtectedRoute component={AdaptiveLocationCampaignDetail} />} />
        {/* Legacy Adaptive Location data-processing paths → redirect */}
        <Route path="/adaptive-location/ingest" component={() => <Redirect to="/data-processing/ingest" replace />} />
        <Route path="/adaptive-location/campaigns/:id">
          {(params: { id: string }) => (
            <Redirect to={`/data-processing/campaigns/${params.id}`} replace />
          )}
        </Route>
        <Route path="/adaptive-location/campaigns" component={() => <Redirect to="/data-processing/campaigns" replace />} />
        <Route path="/creative-space/:assetId" component={() => <ProtectedRoute component={CreativeSpacePicker} />} />
        <Route path="/create/upload" component={() => <ProtectedRoute component={CreateUpload} />} />
        <Route path="/create/:id/analyze" component={() => <ProtectedRoute component={CreateAnalyze} />} />
        <Route path="/create/:id/formats" component={() => <ProtectedRoute component={CreateFormats} />} />
        <Route path="/create/:id/arrange">
          {(params: { id: string }) => <Redirect to={`/create/${params.id}/formats`} replace />}
        </Route>
        <Route path="/create/:id/processing" component={() => <ProtectedRoute component={CreateProcessing} />} />
        <Route path="/create/:id/canva" component={() => <ProtectedRoute component={CreateCanva} />} />
        <Route path="/presets" component={() => <ProtectedRoute component={Presets} />} />
        <Route path="/jobs" component={() => <ProtectedRoute component={Jobs} />} />
        <Route path="/credits" component={() => <ProtectedRoute component={Credits} />} />
        <Route path="/settings" component={() => <ProtectedRoute component={Settings} />} />
        <Route path="/notifications" component={() => <ProtectedRoute component={Notifications} />} />
        <Route path="/telemetry/bbox-crop" component={() => <ProtectedRoute component={BboxCropTelemetry} />} />
        <Route path="/reviews" component={() => <ProtectedRoute component={CustomerReviews} />} />
        <Route path="/reviews/:id" component={() => <ProtectedFullScreen component={CustomerReview} />} />
        <Route path="/account" component={() => <Redirect to="/settings" replace />} />
        <Route path="/billing" component={() => <ProtectedRoute component={Billing} />} />
        <Route path="/referrals" component={() => <ProtectedRoute component={ReferralsPage} />} />
        <Route path="/affiliate" component={() => <ProtectedRoute component={AffiliatePage} />} />
        <Route path="/purchase" component={() => <ProtectedRoute component={Purchase} />} />
        <Route path="/checkout/:id" component={() => <ProtectedRoute component={Checkout} />} />
        <Route path="/team" component={ProtectedTeamRoute} />
        <Route path="/admin" component={() => <Redirect to="/admin/clients" replace />} />
        <Route path="/admin/clients" component={() => <ProtectedAdminRoute component={AdminClients} />} />
        <Route path="/admin/clients/:id" component={() => <ProtectedAdminRoute component={AdminClientDetail} />} />
        <Route path="/admin/reconciliation" component={() => <ProtectedAdminRoute component={AdminReconciliation} />} />
        <Route path="/admin/contact" component={() => <ProtectedAdminRoute component={AdminContact} />} />
        <Route path="/admin/adaptive-location" component={() => <ProtectedAdminRoute component={AdminAdaptiveLocation} />} />
        <Route path="/admin/settings" component={() => <ProtectedAdminRoute component={AdminSettings} />} />
        <Route path="/admin/reports" component={() => <ProtectedAdminRoute component={AdminReports} />} />
        <Route path="/admin/referrals" component={() => <ProtectedAdminRoute component={AdminReferrals} />} />
        <Route path="/admin/affiliates" component={() => <ProtectedAdminRoute component={AdminAffiliates} />} />
        <Route path="/:rest*">
          <div className="p-8">{t("app.comingSoon")}</div>
        </Route>
      </Switch>
    </Suspense>
  );
}

function usesAppShell(location: string) {
  if (/^\/assets\/[^/]+\/creative-space\/?$/.test(location)) return false;
  if (/^\/reviews\/[^/]+\/?$/.test(location)) return false;

  return [
    "/dashboard",
    "/assets",
    "/creative-space",
    "/banner-to-video",
    "/adaptive-location",
    "/data-processing",
    "/create",
    "/presets",
    "/jobs",
    "/credits",
    "/settings",
    "/notifications",
    "/telemetry",
    "/reviews",
    "/account",
    "/billing",
    "/referrals",
    "/affiliate",
    "/purchase",
    "/checkout",
    "/team",
  ].some((prefix) => location === prefix || location.startsWith(`${prefix}/`));
}

function RoutedContent() {
  const [location] = useLocation();
  const { user, isLoading } = useAuth();
  const content = !isLoading && user && usesAppShell(location)
    ? (
      <AppShell>
        <Routes />
      </AppShell>
    )
    : <Routes />;

  return <RouteErrorBoundary>{content}</RouteErrorBoundary>;
}

function App() {
  return (
    <ThemeProvider defaultTheme="dark" storageKey="creative-adapt-theme">
      <TooltipProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <WouterRouter base={basePath}>
              <RoutedContent />
              <CookieNotice />
            </WouterRouter>
            <Toaster />
          </AuthProvider>
        </QueryClientProvider>
      </TooltipProvider>
    </ThemeProvider>
  );
}

export default App;
